import { Component, ElementRef, inject, signal, OnInit, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { GameService, ActiveTab } from '../../core/state/game.service';
import { IconComponent } from '../../shared/ui/icon/icon.component';
import { SettingsModalComponent } from '../../shared/ui/settings-modal/settings-modal.component';
import { NewsReadState } from '../../core/state/news-read.state';
import { Language } from '../../core/i18n/translations';
import { CompetitionService } from '../../core/http/competition.service';
import { extractActiveCompetitionContext } from '../../core/http/competition.api';
import { selectClub } from '../../core/state/club-session';

interface CompetitionChoice {
  id: string;
  name: string;
  type: 'GLOBAL' | 'PRIVATE';
  userClubId: string;
  userClubName: string;
  canPlay: boolean;
}

import { ClubShieldComponent } from '../../shared/ui/club-shield/club-shield.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, IconComponent, SettingsModalComponent, ClubShieldComponent],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'onEscape()'
  }
})
export class NavbarComponent implements OnInit {
  readonly game = inject(GameService);
  readonly newsRead = inject(NewsReadState);
  private readonly elementRef = inject(ElementRef);
  private readonly competitionService = inject(CompetitionService);
  readonly competitionChoices = signal<CompetitionChoice[]>([]);
  readonly isLoadingCompetitions = signal(false);
  readonly competitionLoadFailed = signal(false);

  constructor() {
    effect((onCleanup) => {
      const userId = this.game.currentUser()?.id || '';
      const leagueId = this.game.currentLeague()?.id || '';
      const clubId = this.game.activeUserClubId() || '';
      untracked(() => this.newsRead.setScope(userId, leagueId, clubId));
      if (!userId || !leagueId || !clubId) return;
      let pending = false;
      const refresh = async () => {
        if (pending || document.visibilityState === 'hidden') return;
        pending = true;
        try { await this.newsRead.refresh(userId); } finally { pending = false; }
      };
      untracked(() => { void refresh(); });
      const interval = window.setInterval(() => { void refresh(); }, 60_000);
      const onFocus = () => { void refresh(); };
      const onStorage = (event: StorageEvent) => {
        if (!event.key || event.key.startsWith('lom.news.read.v1:')) this.newsRead.syncStorage();
      };
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onFocus);
      window.addEventListener('storage', onStorage);
      onCleanup(() => {
        window.clearInterval(interval);
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onFocus);
        window.removeEventListener('storage', onStorage);
      });
    });
    // Sincroniza automáticamente el balance de tesorería del club en cuanto se resuelva o cambie
    effect(() => {
      const clubId = this.game.activeUserClubId() || this.game.userClub()?.id;
      if (clubId && clubId !== 'default') {
        this.game.fetchClubBalance(clubId);
      }
    });
  }

  ngOnInit(): void {
    const clubId = this.game.activeUserClubId() || this.game.userClub()?.id;
    if (clubId && clubId !== 'default') {
      this.game.fetchClubBalance(clubId);
    }
  }

  readonly isUserMenuOpen = signal<boolean>(false);
  readonly isContextSwitcherOpen = signal<boolean>(false);
  readonly isSettingsModalOpen = signal<boolean>(false);

  readonly navItems: { tab: ActiveTab; labelKey: string; iconName: string }[] = [
    { tab: 'competition', labelKey: 'nav.competition', iconName: 'trophy' },
    { tab: 'news', labelKey: 'nav.news', iconName: 'newspaper' },
    { tab: 'squad', labelKey: 'nav.squad', iconName: 'users' },
    { tab: 'market', labelKey: 'nav.market', iconName: 'coins' },
    { tab: 'club', labelKey: 'nav.club', iconName: 'building' },
  ];

  openSettingsModal(): void {
    this.isSettingsModalOpen.set(true);
    this.closeUserMenu();
  }

  closeSettingsModal(): void {
    this.isSettingsModalOpen.set(false);
  }

  topUpCredits(amount: number): void {
    this.game.topUpCredits(amount);
  }

  paySubscription(): void {
    this.game.payMonthlySubscription();
  }

  async toggleContextSwitcher(): Promise<void> {
    this.isContextSwitcherOpen.update((v) => !v);
    if (this.isContextSwitcherOpen()) {
      this.isUserMenuOpen.set(false);
      if (this.isLoadingCompetitions()) return;
      this.isLoadingCompetitions.set(true);
      this.competitionLoadFailed.set(false);
      this.competitionChoices.set([]);
      try {
        const state = await this.competitionService.getMyCompetitions(true);
        const global = extractActiveCompetitionContext(state);
        const choices: CompetitionChoice[] = [];
        if (global.status === 'JOINED' && global.clubId) {
          choices.push({ id: global.competitionId || 'comp-global-01',
            name: state.global?.name || 'Liga Global de Mánagers', type: 'GLOBAL',
            userClubId: global.clubId, userClubName: global.clubName || '', canPlay: true });
        }
        for (const league of state.privateLeagues || []) {
          if (league.membershipStatus !== 'JOINED' || !league.club?.id) continue;
          choices.push({ id: league.privateLeagueId, name: league.name, type: 'PRIVATE',
            userClubId: league.club.id, userClubName: league.club.name,
            canPlay: league.capabilities?.play === true });
        }
        this.competitionChoices.set(choices);
      } catch {
        this.competitionLoadFailed.set(true);
      } finally {
        this.isLoadingCompetitions.set(false);
      }
    }
  }

  closeContextSwitcher(): void {
    this.isContextSwitcherOpen.set(false);
  }

  switchContext(clubId: string): void {
    const league = this.competitionChoices().find(choice => choice.userClubId === clubId);
    if (!league?.canPlay) return;
    this.closeContextSwitcher();
    if (clubId === this.game.activeUserClubId()) return;
    selectClub(clubId);
    window.location.assign('/competition');
  }

  toggleUserMenu(): void {
    this.isUserMenuOpen.update((v) => !v);
    if (this.isUserMenuOpen()) {
      this.isContextSwitcherOpen.set(false);
    }
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  async logout(): Promise<void> {
    this.closeUserMenu();
    await this.game.logout();
  }

  selectLanguage(lang: Language): void {
    this.game.i18n.setLanguage(lang);
  }

  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.isUserMenuOpen()) {
      const insideMenu = target.closest('#user-menu-dropdown, #mobile-user-menu, #btn-user-settings');
      if (!insideMenu) {
        this.closeUserMenu();
      }
    }

    if (this.isContextSwitcherOpen()) {
      const insideContext = target.closest('#context-switcher-dropdown, #btn-switch-league, #btn-switch-league-mobile');
      if (!insideContext) {
        this.closeContextSwitcher();
      }
    }
  }

  onEscape(): void {
    this.closeSettingsModal();
    this.closeUserMenu();
    this.closeContextSwitcher();
  }
}

